<script setup>
import { scrollToProductInCarousel } from '#wa/utils/product'

import homepageRestaurantImage from '#wa/assets/images/homepage-restaurant-image.png'

const { $axios, $store } = useNuxtApp()
const productResults = useProductResults()

const restaurantOccasions = ref([])
const activeRestaurant = ref(0)
const carouselRestaurantPrefix = 'restaurant-'

const restaurantFetchCount = computed(() => $store.getters.restaurantFetchCount)
const restaurantItems = computed(() => {
  const occasion = restaurantOccasions.value[activeRestaurant.value]
  return occasion?.products || []
})

const restaurantImage = computed(() => {
  const activeOccasion =
    restaurantOccasions.value[activeRestaurant.value]?.extended_data

  if (activeOccasion?.michelin_image) {
    return `${useRuntimeConfig().public.cloudfrontBase}${activeOccasion.michelin_image}`
  }

  return homepageRestaurantImage
})

// Methods
const setActiveRestaurant = (activeRestaurantIndex) => {
  activeRestaurant.value = activeRestaurantIndex
}

const fetchMichelinRestaurantOccasions = async () => {
  try {
    restaurantOccasions.value = (
      await $axios.get('/api/homepage/michelin-restaurant-data')
    ).data.michelin_restaurant_occasions
  } catch (error) {
    console.error('Unable to fetch michelin restaurant occasions data', error)
  }
}

const fetchRestaurantsProducts = async (index) => {
  const occasion = restaurantOccasions.value[index]
  if (!occasion.products) {
    try {
      const response = await $fetch(
        `/api/catalog/search/?occasions=${occasion.slug}&page_size=12`,
      )
      occasion.products = response.results
      restaurantFetchCount.value++
    } catch (error) {
      console.error('Error fetching restaurant products:', error)
    }
  }
}

const handleActiveRestaurantChange = async (index) => {
  await fetchRestaurantsProducts(index)
  scrollToProductInCarousel(nextTick)
}

watch(restaurantOccasions, (newValue) => {
  if (newValue.length > 0) {
    handleActiveRestaurantChange(activeRestaurant.value)
  }
})

watch(activeRestaurant, async (newValue, oldValue) => {
  if (!restaurantOccasions.value[newValue]?.products) {
    const slug = restaurantOccasions.value[newValue]?.slug

    try {
      // Fetch products dynamically for the selected restaurant occasion
      const response = await $axios.get(
        `/api/catalog/search/?occasions=${slug}&page_size=12`,
      )

      restaurantOccasions.value[newValue].products = response.data.results

      await $store.dispatch('incrementRestaurantFetchCount')
    } catch (error) {
      console.error('Error fetching products for restaurant occasion:', error)
    }
  }
  scrollToProductInCarousel(nextTick)
})

onMounted(async () => {
  await fetchMichelinRestaurantOccasions()
  // Handle scenario where productIDInView and carouselInView are already set
  if (
    productResults.productIDInView &&
    productResults.productIDInView.value !== undefined &&
    productResults.carouselInView &&
    productResults.carouselInView.value !== undefined
  ) {
    if (
      productResults.carouselInView.value.startsWith(carouselRestaurantPrefix)
    ) {
      const match = productResults.carouselInView.value.match(/(\d+)$/) // Extract the index from the carousel ID
      if (match) {
        activeRestaurant.value = parseInt(match[1], 10) // Set active restaurant index
        await nextTick() // Ensure DOM updates before scrolling
        scrollToProductInCarousel(nextTick) // Scroll to the product in the correct carousel
      }
    }
  }
})
</script>

<template>
  <div>
    <section class="home-restaurants">
      <div class="row image-copy-split">
        <div class="columns large-6 col-copy">
          <h2>
            Collaborations<br />
            with Award-Winning<br />
            Restaurants
          </h2>
        </div>
        <div
          class="columns large-6 image-col"
          :style="{
            'background-image': `url('${restaurantImage}')`,
            'background-size': 'cover',
          }"
        />
      </div>
    </section>
    <section class="home-standard">
      <div class="row">
        <div class="columns text-center">
          <div class="small-caps-text">Shop by Restaurant</div>
          <h2>Exclusive Collections</h2>
          <div class="carousel-container site-width">
            <div id="restaurant-tabs" class="tabs">
              <div
                v-for="(
                  { name, slug, extended_data }, index
                ) in restaurantOccasions"
                :id="slug"
                :key="index"
                v-scroll-to="{
                  el: `#${slug}`,
                  container: '#restaurant-tabs',
                  offset: -100,
                  x: true,
                  y: false,
                }"
                class="tab"
                :class="{ active: index === activeRestaurant }"
                :data-collection="slug"
                @click="setActiveRestaurant(index)"
              >
                {{ extended_data.michelin_restaurant_name || name }}
              </div>
            </div>
            <div
              :key="`${carouselRestaurantPrefix}carousel-${activeRestaurant}${restaurantFetchCount}`"
            >
              <ProductGridCarousel
                :carousel-id="carouselRestaurantPrefix + activeRestaurant"
                :items="restaurantItems"
                :title="null"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
